import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Drawer, Dropdown, Row, Space, Tag } from 'antd';
import { TbChevronRight, TbCopy, TbExternalLink, TbFilePlus, TbFileText, TbLink, TbQuote } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useSourceQuoteActions } from '@/hooks/useSourceQuoteActions';
import { useSourceDocument } from '@/queries/tanstack/api/sourceDocument';
import { EmptySection } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DocumentReaderFooter from '@/shared/components/layouts/footers/DocumentReaderFooter';
import QuoteMemoDropdownPanel from '@/shared/components/memos/QuoteMemoDropdownPanel';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { useMemoStore } from '@/stores';

import type { SourceDocumentReaderProps, SourceQuoteSelection } from './types';
import {
    applyLightSourceDocumentSurface,
    buildInlineSourceDocument,
    clearSourceDocumentParams,
    getLinkedSourceDocument,
    getSourceDocumentLink,
    getSourceKindLabel,
    getSourceQuoteHtml,
    normalizeSourceText,
    SOURCE_DOCUMENT_BACKGROUND,
} from './utils';

const SourceDocumentReader: FC<SourceDocumentReaderProps> = ({ navigationAction, onClose, source }) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const frameDocumentRef = useRef<Document | null>(null);
    const viewerRef = useRef<HTMLDivElement>(null);
    const [selectedQuote, setQuoteSelection] = useState<SourceQuoteSelection & { sourceKey: string }>();
    const { token } = useAntDesignTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { fetchMemos, memos, loading: loadingMemos } = useMemoStore();
    const linkedSource = useMemo(() => getLinkedSourceDocument(location.search), [location.search]);
    const activeSource = source ?? linkedSource;
    const sourceKey = JSON.stringify([activeSource?.canonicalUrl, activeSource?.title, activeSource?.documentHtml]);
    const quoteSelection = selectedQuote?.sourceKey === sourceKey ? selectedQuote : undefined;
    const documentQuery = useSourceDocument(activeSource?.documentHtml ? undefined : activeSource?.canonicalUrl);
    const inlineDocument =
        activeSource && !activeSource.canonicalUrl && !['news', 'press-release'].includes(activeSource.kind)
            ? buildInlineSourceDocument(activeSource)
            : '';
    const documentHtml = activeSource?.documentHtml || documentQuery.data || inlineDocument;
    const loading = documentQuery.isFetching && !documentQuery.data;

    useEffect(() => {
        if (activeSource) {
            void fetchMemos();
        }
    }, [activeSource, fetchMemos]);

    const handleClose = useCallback(() => {
        onClose();
        setQuoteSelection(undefined);
        if (linkedSource) {
            navigate(
                {
                    pathname: location.pathname,
                    search: clearSourceDocumentParams(location.search),
                    hash: location.hash,
                },
                { replace: true },
            );
        }
    }, [linkedSource, location.hash, location.pathname, location.search, navigate, onClose]);

    const sourceLink = useCallback(
        (quote?: string) => (activeSource ? getSourceDocumentLink(activeSource, quote) : window.location.href),
        [activeSource],
    );

    const openNavigationAction = useCallback(() => {
        if (!navigationAction) {
            return;
        }

        if (navigationAction.external) {
            window.open(navigationAction.href, '_blank', 'noopener,noreferrer');
            return;
        }

        onClose();
        navigate(navigationAction.href);
    }, [navigate, navigationAction, onClose]);

    const readFrameSelection = useCallback(() => {
        const iframe = iframeRef.current;
        const viewer = viewerRef.current;
        const selection = iframe?.contentWindow?.getSelection();
        if (!iframe || !viewer || !selection || selection.isCollapsed || !selection.rangeCount) {
            setQuoteSelection(undefined);
            return;
        }
        const text = normalizeSourceText(selection.toString());
        if (text.length < 2) {
            return setQuoteSelection(undefined);
        }
        const rangeRect = selection.getRangeAt(0).getBoundingClientRect();
        const iframeRect = iframe.getBoundingClientRect();
        const viewerRect = viewer.getBoundingClientRect();
        setQuoteSelection({
            sourceKey,
            text,
            left: Math.max(12, iframeRect.left - viewerRect.left + rangeRect.left),
            top: Math.max(12, iframeRect.top - viewerRect.top + rangeRect.top - 42),
        });
    }, [sourceKey]);

    const handleFrameLoad = useCallback(() => {
        setQuoteSelection(undefined);
        const frameDocument = iframeRef.current?.contentDocument ?? null;
        const previousDocument = frameDocumentRef.current;

        previousDocument?.removeEventListener('mouseup', readFrameSelection);
        previousDocument?.removeEventListener('keyup', readFrameSelection);
        frameDocumentRef.current = frameDocument;

        if (frameDocument) {
            if (activeSource?.kind !== 'news' && activeSource?.kind !== 'press-release') {
                applyLightSourceDocumentSurface(frameDocument);
            }
            frameDocument.addEventListener('mouseup', readFrameSelection);
            frameDocument.addEventListener('keyup', readFrameSelection);
        }
    }, [activeSource?.kind, readFrameSelection]);

    useEffect(
        () => () => {
            frameDocumentRef.current?.removeEventListener('mouseup', readFrameSelection);
            frameDocumentRef.current?.removeEventListener('keyup', readFrameSelection);
        },
        [readFrameSelection],
    );

    const quoteHtml = useCallback(
        (quote: string) => (activeSource ? getSourceQuoteHtml(activeSource, quote, sourceLink(quote)) : ''),
        [activeSource, sourceLink],
    );

    const { appendQuoteToMemo, busy, copyQuote, copySourceLink, createMemoFromQuote, pendingAction } =
        useSourceQuoteActions({
            getQuoteHtml: quoteHtml,
            getSourceLink: sourceLink,
            memoTitle: `${activeSource?.title ?? 'Source'} Notes`,
            selectedText: quoteSelection?.text,
            sourceKey,
        });

    const quoteMemoPanel = (
        <QuoteMemoDropdownPanel
            active={Boolean(quoteSelection)}
            memos={memos}
            pendingAction={pendingAction}
            loadingMemos={loadingMemos}
            onCopy={copyQuote}
            onCreate={() => createMemoFromQuote()}
            onSelectMemo={(memo) => appendQuoteToMemo(memo)}
        />
    );

    const navigationButton = () =>
        navigationAction ? (
            <Button
                icon={navigationAction.external ? <TbExternalLink /> : <TbChevronRight />}
                onClick={openNavigationAction}
                size='small'
                type='primary'
            >
                {navigationAction.label}
            </Button>
        ) : null;

    const actionRow = () => (
        <Row
            align='middle'
            style={{
                padding: '0.75rem 1rem',
                borderBottom: `1px solid ${token.colorBorder}`,
            }}
            wrap={false}
        >
            <Col flex='auto'>
                <Space size={8}>
                    {navigationButton()}
                    <Button
                        size='small'
                        icon={<TbLink />}
                        disabled={busy}
                        loading={pendingAction?.type === 'copy-source'}
                        onClick={copySourceLink}
                    >
                        Copy Source Link
                    </Button>
                    {activeSource?.canonicalUrl && activeSource.canonicalUrl !== navigationAction?.href ? (
                        <a href={activeSource.canonicalUrl} target='_blank' rel='noreferrer'>
                            <Button size='small' icon={<TbExternalLink />}>
                                Open Original
                            </Button>
                        </a>
                    ) : null}
                </Space>
            </Col>
            <Col flex='none'>
                <Dropdown popupRender={() => quoteMemoPanel} trigger={['click']} disabled={!quoteSelection || busy}>
                    <DropdownButton
                        size='small'
                        type='primary'
                        disabled={!quoteSelection || busy}
                        loading={busy && pendingAction?.type !== 'copy-source'}
                        icon={<TbQuote />}
                    >
                        Quote Selection
                    </DropdownButton>
                </Dropdown>
            </Col>
        </Row>
    );

    return (
        <Drawer
            title={
                activeSource ? (
                    <Space size={8}>
                        <Tag>{getSourceKindLabel(activeSource)}</Tag>
                        <span>{activeSource.title}</span>
                    </Space>
                ) : (
                    'Source'
                )
            }
            placement='right'
            width='64vw'
            open={Boolean(activeSource)}
            onClose={handleClose}
            bodyStyle={{ overflow: 'hidden', padding: 0 }}
            footerStyle={{ borderTop: 0, padding: 0 }}
            footer={
                <DocumentReaderFooter
                    available={Boolean(documentHtml)}
                    date={activeSource?.publishedAt}
                    documentType={activeSource ? getSourceKindLabel(activeSource) : undefined}
                    loading={loading}
                    publisher={activeSource?.publisher}
                    selectedText={quoteSelection?.text}
                    title={activeSource?.sourceEntity?.name || activeSource?.title}
                />
            }
        >
            {loading ? (
                <div style={{ padding: '1.5rem' }}>
                    <BaseSkeleton paragraph={{ rows: 16 }} title />
                </div>
            ) : documentHtml ? (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                    {actionRow()}
                    <div
                        ref={viewerRef}
                        style={{
                            flex: 1,
                            margin: 0,
                            minHeight: 0,
                            overflow: 'hidden',
                            position: 'relative',
                            backgroundColor: SOURCE_DOCUMENT_BACKGROUND,
                        }}
                    >
                        <iframe
                            ref={iframeRef}
                            sandbox='allow-same-origin'
                            title={activeSource?.title || 'Source document'}
                            srcDoc={documentHtml}
                            onLoad={handleFrameLoad}
                            style={{
                                backgroundColor: SOURCE_DOCUMENT_BACKGROUND,
                                border: 0,
                                colorScheme: 'only light',
                                display: 'block',
                                height: '100%',
                                width: '100%',
                            }}
                        />
                        {quoteSelection ? (
                            <div
                                style={{
                                    position: 'absolute',
                                    left: quoteSelection.left,
                                    top: quoteSelection.top,
                                    zIndex: 2,
                                    padding: '0.25rem',
                                    border: `1px solid ${token.colorBorder}`,
                                    borderRadius: token.borderRadius,
                                    backgroundColor: token.colorBgElevated,
                                    boxShadow: token.boxShadowSecondary,
                                }}
                            >
                                <Space size={4}>
                                    <Button
                                        size='small'
                                        type='primary'
                                        icon={<TbFilePlus />}
                                        disabled={busy}
                                        loading={pendingAction?.type === 'create-memo'}
                                        onClick={createMemoFromQuote}
                                    >
                                        New Memo
                                    </Button>
                                    <Dropdown popupRender={() => quoteMemoPanel} trigger={['click']} disabled={busy}>
                                        <DropdownButton
                                            size='small'
                                            disabled={busy}
                                            loading={pendingAction?.type === 'add-to-memo'}
                                        >
                                            Add to Memo
                                        </DropdownButton>
                                    </Dropdown>
                                    <Button
                                        size='small'
                                        aria-label='Copy quote and source'
                                        icon={<TbCopy />}
                                        disabled={busy}
                                        loading={pendingAction?.type === 'copy-quote'}
                                        onClick={copyQuote}
                                    />
                                </Space>
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : (
                <EmptySection
                    height='100%'
                    icon={<TbFileText />}
                    label='Document Unavailable'
                    description='This source could not be loaded in the reader. You can still open the original source.'
                    extra={
                        navigationAction || activeSource?.canonicalUrl ? (
                            <Space size={8}>
                                {navigationButton()}
                                {activeSource?.canonicalUrl && activeSource.canonicalUrl !== navigationAction?.href ? (
                                    <a href={activeSource.canonicalUrl} target='_blank' rel='noreferrer'>
                                        <Button size='small' icon={<TbExternalLink />}>
                                            Open Original
                                        </Button>
                                    </a>
                                ) : null}
                            </Space>
                        ) : null
                    }
                />
            )}
        </Drawer>
    );
};

export type { SourceDocument, SourceDocumentKind, SourceDocumentNavigationAction } from './types';
export default SourceDocumentReader;
